import './DRHistory.css';
import { useDRHistorySimple } from '../../hooks/useDRHistorySimple';
import Calendar from '../../components/Calendar';
import dayjs from 'dayjs';
import { useState, useMemo, useEffect } from 'react';
import { SkeletonTable } from '../../components/Skeleton';
import Pagination from '../../components/Pagination/Pagination';
import { useSearchParams } from 'react-router-dom';
import DrHistoryTableItem from './components/DrHistoryTableItem';
const HISTORY_PAGE_SIZE = 20;

const DRHistory = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [historyPage, setHistoryPage] = useState(1);
  
  // URL에서 날짜 범위 읽기 (없으면 기본값: 3개월 전부터 오늘까지)
  const getInitialDateRange = () => {
    const startDateParam = searchParams.get('startDate');
    const endDateParam = searchParams.get('endDate');
    
    if (startDateParam && endDateParam) {
      const startDate = dayjs(startDateParam, 'YYYYMMDD');
      const endDate = dayjs(endDateParam, 'YYYYMMDD');
      if (startDate.isValid() && endDate.isValid()) {
        return [startDate, endDate];
      }
    }
    
    // 기본값: 3개월 전부터 오늘까지
    return [dayjs().subtract(3, 'month'), dayjs()];
  };

  const [dateRange, setDateRange] = useState(getInitialDateRange);

  // URL 파라미터가 변경되면 dateRange 업데이트
  useEffect(() => {
    const newDateRange = getInitialDateRange();
    setDateRange(newDateRange);
  }, [searchParams.get('startDate'), searchParams.get('endDate')]);

  // 날짜 범위 변경 핸들러
  const handleDateRangeChange = (newDateRange) => {
    if (newDateRange && Array.isArray(newDateRange) && newDateRange.length === 2) {
      setDateRange(newDateRange);
      // URL 파라미터 업데이트
      const current = Object.fromEntries(searchParams.entries());
      setSearchParams({
        ...current,
        startDate: dayjs(newDateRange[0]).format('YYYYMMDD'),
        endDate: dayjs(newDateRange[1]).format('YYYYMMDD')
      });
    }
  };

  // API 호출용 날짜 형식
  const startDate = useMemo(() => dayjs(dateRange[0]).format('YYYYMMDD'), [dateRange]);
  const endDate = useMemo(() => dayjs(dateRange[1]).format('YYYYMMDD'), [dateRange]);

  const { drHistoryRangeData, drSettlementList, drHistoryRangeLoading, drHistoryRangeError } 
  = useDRHistorySimple({
    startDate,
    endDate
  });

  const responseList = drHistoryRangeData?.responseList || [];

  const historyTotalPages = Math.max(1, Math.ceil(responseList.length / HISTORY_PAGE_SIZE));

  const paginatedHistoryList = useMemo(() => {
    const start = (historyPage - 1) * HISTORY_PAGE_SIZE;
    return responseList.slice(start, start + HISTORY_PAGE_SIZE);
  }, [responseList, historyPage]);

  useEffect(() => {
    setHistoryPage(1);
  }, [startDate, endDate]);

  useEffect(() => {
    if (historyPage > historyTotalPages) {
      setHistoryPage(historyTotalPages);
    }
  }, [historyPage, historyTotalPages]);

  if (drHistoryRangeLoading) {
    return (
      <div className="dr-history-container">
        <div className="primary-table">
          <p>데이터를 불러오는 중...</p>
        </div>
      </div>
    );
  }

  if (drHistoryRangeError) {
    return (
      <div className="dr-history-container">
        <div className="primary-table">
          <p>데이터를 불러오는 중 오류가 발생했습니다: {drHistoryRangeError.message}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="dr-history-container page-container">
      {/* Calendar Filter Section */}
      <div className="calendar-filter-section">
        <div className="calendar-filters">
          <Calendar
            mode="range"
            value={dateRange}
            onChange={handleDateRangeChange}
            placeholder="날짜 범위를 선택하세요"
            className="calendar-range"
          />
        </div>
      </div>
      <div className="text-b3-400 text-gray-600 mb-20 ">
        <p className="text-primary">정산 데이터는 발령일 기준 익일 07시10분에 업데이트됩니다.</p>
      </div>

      {/* Data Table Section */}
      {drHistoryRangeLoading && (
        <SkeletonTable rows={8} columns={8} />
      )}
      {!drHistoryRangeLoading && !drHistoryRangeError && (
        <>
        <p className="text-c1-700 text-gray-700 mb-10">
          Total <span className="text-primary">{responseList.length.toLocaleString()}</span> 회
        </p>
        <div className="primary-table">
          <table>
            <thead>
              <tr>
                <th width="50px"></th>
                <th>날짜</th>
                <th>시작시간</th>
                <th>종료시간</th>
                <th>지속시간</th>
                <th width="130px"></th>
              </tr>
            </thead>
            <tbody>
              {responseList?.length > 0 ? (
                paginatedHistoryList.map((item, index) => {
                  const globalIndex = (historyPage - 1) * HISTORY_PAGE_SIZE + index;
                  return (
                    <DrHistoryTableItem
                      key={item.id ?? `${globalIndex}-${item.date}-${item.startTime}`}
                      item={item}
                      drSettlement={item.settlement ?? drSettlementList[globalIndex]}
                      index={globalIndex}
                    />
                  );
                })
              ) : (
                <tr>
                  <td colSpan="5">데이터가 없습니다.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {responseList.length > 0 && (
          <div className="pagination-wrapper">
            <Pagination
              currentPage={historyPage}
              totalPages={historyTotalPages}
              onPageChange={setHistoryPage}
              size="medium"
            />
          </div>
        )}
        </>
      )}
    </div>
  );
};

export default DRHistory; 